import { useState, useEffect, useRef } from "react";
import { AlertTriangle, CheckCircle, CheckSquare, PenLine, Search, Square, Star } from "lucide-react";
import { supabase } from "./supabase";
import { KO, EN, fmtDate, sendPushToEmployee, sendPushToAdmin } from "./helpers";

export function EvalManager({t,lang,isAdmin,empName,refreshKey}){
  const E=t.eval;
  const [subTab,setSubTab]=useState("perf");

  return(
    <>
      <div className="ph"><div className="pt"><Star size={20}/> {E.title}</div><div className="ps">{E.sub}</div></div>
      <div className="tabs" style={{marginBottom:20}}>
        <button className={`tab ${subTab==="perf"?"active":""}`} onClick={()=>setSubTab("perf")}> {E.tabPerf}</button>
        {isAdmin&&<button className={`tab ${subTab==="new"?"active":""}`} onClick={()=>setSubTab("new")}> {E.tabNew}</button>}
        {isAdmin&&<button className={`tab ${subTab==="note"?"active":""}`} onClick={()=>setSubTab("note")}> {E.tabNote}</button>}
      </div>
      {subTab==="perf" && <PerfEval key={refreshKey} t={t} lang={lang} isAdmin={isAdmin} empName={empName} refreshKey={refreshKey}/>}
      {subTab==="new"  && <NewEmpEval key={refreshKey} t={t} lang={lang} isAdmin={isAdmin} empName={empName} refreshKey={refreshKey}/>}
      {subTab==="note" && <PerfNotes key={refreshKey} t={t} lang={lang} isAdmin={isAdmin} empName={empName} refreshKey={refreshKey}/>}
    </>
  );
}

function PerfEval({t,lang,isAdmin,empName,refreshKey}){
  const E=t.eval;
  const KO=lang==="kr"; const SP=lang==="es";
  const [employees,setEmp]=useState([]);
  const [evals,setEvals]=useState([]);
  const [tab,setTab]=useState("list");
  const [fe,setFe]=useState("");
  const [searchPerf,setSearchPerf]=useState("");
  const [showSearchPerf,setShowSearchPerf]=useState(false);
  const [editId,setEditId]=useState(null);
  const [uploading,setUploading]=useState(null);
  const [signModal,setSignModal]=useState(null);
  const [signing,setSigning]=useState(false);
  const canvasRef=useRef(null);
  const [isDrawing,setIsDrawing]=useState(false);

  useEffect(()=>{
    if(signModal&&canvasRef.current){
      const ctx=canvasRef.current.getContext("2d");
      ctx.clearRect(0,0,canvasRef.current.width,canvasRef.current.height);
    }
  },[signModal]);
  const defRatings=E.items.reduce((a,i)=>({...a,[i.key]:"3"}),{});
  const defForm={emp_name:"",evaluator:"",period:fmtDate(new Date()).slice(0,7),eval_date:fmtDate(new Date()),
    goals:"",outstanding:"",improve:"",readiness:"",
    ratings:{...defRatings},descriptions:{},
    next_goals:"",next_dev:"",emp_comment:"",signed_url:""};
  const [form,setForm]=useState(defForm);

  useEffect(()=>{
    supabase.from("employees").select("*").order("join_date",{ascending:true}).then(({data})=>data&&setEmp(data));
    fetchEvals();
  },[refreshKey]);
  async function fetchEvals(){let q=supabase.from("perf_evals").select("*");if(!isAdmin&&empName)q=q.eq("emp_name",empName);const{data}=await q.order("eval_date",{ascending:false});if(data)setEvals(data);}
  function sf(k,v){setForm(p=>({...p,[k]:v}));}
  function sr(k,v){setForm(p=>({...p,ratings:{...p.ratings,[k]:v}}))}
  function sd(k,v){setForm(p=>({...p,descriptions:{...p.descriptions,[k]:v}}))}

  function startEdit(ev){
    setForm({emp_name:ev.emp_name,evaluator:ev.evaluator||"",period:ev.period||"",eval_date:ev.eval_date||"",
      goals:ev.goals||"",outstanding:ev.outstanding||"",improve:ev.improve||"",readiness:ev.readiness||"",
      ratings:ev.ratings||{...defRatings},descriptions:ev.descriptions||{},
      next_goals:ev.next_goals||"",next_dev:ev.next_dev||"",emp_comment:ev.emp_comment||"",signed_url:ev.signed_url||""});
    setEditId(ev.id); setTab("add");
  }

  async function save(){
    if(!form.emp_name||!form.eval_date) return alert(E.empSel);
    if(editId) await supabase.from("perf_evals").update(form).eq("id",editId);
    else{ await supabase.from("perf_evals").insert([form]); await sendPushToEmployee(supabase, form.emp_name||"", "성과평가 등록", "성과평가가 등록되었습니다. 확인 후 서명해주세요.", "Performance Review", "Your performance review has been posted. Please sign it.", "/eval", {title:"Evaluación de desempeño", body:"Se publicó su evaluación de desempeño. Por favor fírmela."}); }
    setForm(defForm); setEditId(null); fetchEvals(); setTab("list");
  }
  async function del(id){if(window.confirm(E.confirmDel)){await supabase.from("perf_evals").delete().eq("id",id);fetchEvals();}}

  async function uploadSigned(id,file){
    setUploading(id);
    try{
      const ext=file.name.split(".").pop();
      const path=`perf_signed/${id}_${Date.now()}.${ext}`;
      const{error}=await supabase.storage.from("hr-files").upload(path,file,{upsert:true});
      if(error)throw error;
      const{data:u}=supabase.storage.from("hr-files").getPublicUrl(path);
      await supabase.from("perf_evals").update({signed_url:u.publicUrl}).eq("id",id);
      fetchEvals();
      alert(lang==="kr"?"업로드 완료!":lang==="es"?"¡Carga completa!":"Upload complete!");
    }catch(e){alert((lang==="kr"?"실패: ":lang==="es"?"Error: ":"Failed: ")+e.message);}
    setUploading(null);
  }

  function startDraw(e){
    setIsDrawing(true);
    const canvas=canvasRef.current;
    if(!canvas) return;
    const ctx=canvas.getContext("2d");
    const r=canvas.getBoundingClientRect();
    const scaleX=canvas.width/r.width;
    const scaleY=canvas.height/r.height;
    const clientX=e.touches?e.touches[0].clientX:e.clientX;
    const clientY=e.touches?e.touches[0].clientY:e.clientY;
    ctx.beginPath();
    ctx.moveTo((clientX-r.left)*scaleX,(clientY-r.top)*scaleY);
  }
  function draw(e){
    if(!isDrawing) return;
    e.preventDefault();
    const canvas=canvasRef.current;
    if(!canvas) return;
    const ctx=canvas.getContext("2d");
    const r=canvas.getBoundingClientRect();
    const scaleX=canvas.width/r.width;
    const scaleY=canvas.height/r.height;
    const clientX=e.touches?e.touches[0].clientX:e.clientX;
    const clientY=e.touches?e.touches[0].clientY:e.clientY;
    ctx.lineWidth=2.5; ctx.lineCap="round"; ctx.lineJoin="round"; ctx.strokeStyle="#1a1a1a";
    ctx.lineTo((clientX-r.left)*scaleX,(clientY-r.top)*scaleY);
    ctx.stroke();
  }
  function endDraw(){ setIsDrawing(false); }
  function clearCanvas(){
    const canvas=canvasRef.current;
    if(!canvas) return;
    canvas.getContext("2d").clearRect(0,0,canvas.width,canvas.height);
  }
   async function submitSignature(){
    const canvas=canvasRef.current;
    if(!canvas){ alert("Canvas error"); return; }
    const imgData=canvas.getContext("2d").getImageData(0,0,canvas.width,canvas.height).data;
    if([...imgData].every(v=>v===0)){ alert(KO?"서명을 그려주세요.":SP?"Por favor dibuje su firma.":"Please draw your signature."); return; }
    setSigning(true);
    try{
      const blob=await new Promise(res=>canvas.toBlob(res,"image/png"));
      const path=`perf_signed/${signModal}_sig_${Date.now()}.png`;
      const{error}=await supabase.storage.from("hr-files").upload(path,blob,{upsert:true,contentType:"image/png"});
      if(error) throw error;
      const{data:u}=supabase.storage.from("hr-files").getPublicUrl(path);
      const signDate=new Date().toLocaleDateString("en-US");
      if(isAdmin){
        await supabase.from("perf_evals").update({evaluator_signed_url:u.publicUrl,evaluator_signed_at:signDate}).eq("id",signModal);
        // 평가자(관리자) 서명 완료 → 직원에게 서명 요청 알림
        const rec=evals.find(e=>e.id===signModal);
        if(rec?.emp_name){
          await sendPushToEmployee(supabase, rec.emp_name, "성과평가 서명 요청", `${rec.period||""} 성과평가 평가자 서명이 완료되었습니다. 확인 후 서명해주세요.`, "Performance Review", `Evaluator has signed your ${rec.period||""} performance review. Please review and sign.`, "/eval", {title:"Evaluación de desempeño", body:`El evaluador firmó su evaluación de desempeño ${rec.period||""}. Por favor revísela y fírmela.`});
        }
      } else {
        await supabase.from("perf_evals").update({signed_url:u.publicUrl,signed_at:signDate}).eq("id",signModal);
        // 직원 서명 완료 → 관리자에게 알림
        const rec=evals.find(e=>e.id===signModal);
        if(rec?.emp_name){
          await sendPushToAdmin(supabase, "성과평가 서명 완료", `${rec.emp_name}님이 ${rec.period||""} 성과평가에 서명했습니다.`, "Performance Review Signed", `${rec.emp_name} has signed the ${rec.period||""} performance review.`, "/eval", {title:"Evaluación de desempeño firmada", body:`${rec.emp_name} firmó la evaluación de desempeño ${rec.period||""}.`});
        }
      }
      setSignModal(null);
      setSigning(false);
      await fetchEvals();
    }catch(err){ setSigning(false); alert("Error: "+err.message); }
  }

  function downloadPdf(rec){
    const ratingLabels={"5":"5","4":"4","3":"3","2":"2","1":"1","N/A":"N/A"};
    const rows=EN.eval.items.map((item,i)=>`
      <tr>
        <td>${i+1}. ${item.label}</td>
        <td style="text-align:center">${["5","4","3","2","1","N/A"].map(v=>`${v===(rec.ratings?.[item.key]||"3")?"&#9745;":"&#9744;"} ${v}`).join("  ")}</td>
        <td>${rec.descriptions?.[item.key]||""}</td>
      </tr>`).join("");
    const footer="This executed document serves as official legal proof of performance evaluation completion and may be used for employment records, personnel decisions, legal proceedings, and HR documentation purposes.";
    const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Performance Evaluation - ${rec.emp_name}</title>
    <style>
      @page{margin:0.85in 0.75in 0.75in; @bottom-center{content:"This executed document serves as official legal proof of performance evaluation completion and may be used for employment records, personnel decisions, legal proceedings, and HR documentation purposes.";font-size:6.5pt;color:#555;font-family:Arial,sans-serif;text-align:center;}}
      body{font-family:Arial,sans-serif;font-size:10pt;color:#222;}
      .ph{display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #aaa;padding-bottom:6px;margin-bottom:10px;}
      .logo{font-weight:900;font-size:13pt;letter-spacing:1px;}
      .docno{font-size:8pt;color:#888;}
      h1{text-align:center;font-size:15pt;margin:8px 0 14px;}
      .section{font-weight:700;margin:12px 0 6px;font-size:11pt;}
      table{width:100%;border-collapse:collapse;font-size:9pt;margin-bottom:8px;}
      th{background:#1e3a5f;color:white;padding:5px 8px;text-align:left;}
      td{border:1px solid #ccc;padding:5px 8px;}
      .info td:first-child{font-weight:700;background:#f0f4f9;width:120px;}
      .line{border:none;border-bottom:1px solid #999;width:100%;margin:10px 0 4px;height:14px;display:block;}
      .sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:12px;}
      .sig-box{border:1px solid #ccc;padding:0;overflow:hidden;}
      .sig-box h3{background:#1e3a5f;color:white;margin:0 0 10px;padding:5px 10px;font-size:9pt;}
      .sig-box-body{padding:0 10px 10px;}
    </style></head><body>
    <div class="ph"><div class="logo">SPRINGBLINDS</div><div class="docno">Document No.: SB-PEF-001</div></div>
    <h1>Performance Evaluation Form</h1>
    <table class="info">
      <tr><td>Employee Name</td><td>${rec.emp_name}</td><td>Position</td><td>${employees.find(e=>e.name===rec.emp_name)?.position||""}</td></tr>
      <tr><td>Evaluator</td><td>${rec.evaluator||""}</td><td>Evaluation Period</td><td>${rec.period||""}</td></tr>
    </table>
    <div class="section">1. Goals and Assessment</div>
    <p style="font-size:9pt;margin-bottom:6px"><b>Performance Period Goals:</b> ${rec.goals||""}</p>
    <p style="font-size:9pt;margin-bottom:4px"><b>Areas of Outstanding Performance:</b></p><span class="line">${rec.outstanding||""}</span>
    <p style="font-size:9pt;margin-bottom:4px"><b>Areas Requiring Improvement:</b></p><span class="line">${rec.improve||""}</span>
    <p style="font-size:9pt;margin-bottom:4px"><b>Readiness for Expanded Responsibilities:</b></p><span class="line">${rec.readiness||""}</span>
    <div class="section">2. Performance Competencies</div>
    <p style="font-size:8pt;margin-bottom:6px">Rating Scale: 5=Exceptional | 4=Highly Effective | 3=Proficient | 2=Inconsistent | 1=Unsatisfactory | N/A=Not Applicable</p>
    <table><thead><tr><th>Competency</th><th style="width:200px">Rating</th><th>Brief Description</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="section">3. Goal Setting and Development Plan</div>
    <p style="font-size:9pt;margin-bottom:4px"><b>Next Year's Performance Goals:</b></p><span class="line">${rec.next_goals||""}</span>
    <p style="font-size:9pt;margin-bottom:4px"><b>Next Year's Development Goals:</b></p><span class="line">${rec.next_dev||""}</span>
    <div style="page-break-before:always"></div>
    <div class="section">4. Employee Comments</div>
    <span class="line">${rec.emp_comment||""}</span>
    <div class="sig-grid">
      <div class="sig-box">
        <h3>EMPLOYEE</h3>
        <div class="sig-box-body">
          <p>Signature:</p>
          ${rec.signed_url?`<img src="${rec.signed_url}" style="height:60px;max-width:100%;object-fit:contain;display:block;margin-bottom:4px;" crossorigin="anonymous"/>`:`<span class="line"/>`}
          <p>Print Name: <b>${rec.emp_name||"___________________"}</b></p>
          <p>Date: <b>${rec.signed_at||(rec.signed_url?new Date().toLocaleDateString("en-US"):"________")}</b></p>
        </div>
      </div>
      <div class="sig-box">
        <h3>EVALUATOR</h3>
        <div class="sig-box-body">
          <p>Signature:</p>
          ${rec.evaluator_signed_url?`<img src="${rec.evaluator_signed_url}" style="height:60px;max-width:100%;object-fit:contain;display:block;margin-bottom:4px;" crossorigin="anonymous"/>`:`<span class="line"/>`}
          <p>Print Name: <b>${rec.evaluator||"___________________"}</b></p>
          <p>Date: <b>${rec.evaluator_signed_at||"________"}</b></p>
        </div>
      </div>
    </div>
    <script>window.onload=()=>{window.print();}</script>
    </body></html>`;
    const w=window.open("","_blank"); w.document.write(html); w.document.close();
  }

  const filtered=evals.filter(e=>{
    if(fe&&e.emp_name!==fe) return false;
    if(searchPerf){const q=searchPerf.toLowerCase();if(!e.emp_name?.toLowerCase().includes(q)&&!e.evaluator?.toLowerCase().includes(q)&&!e.period?.toLowerCase().includes(q)) return false;}
    return true;
  });
  const rOpts=["5","4","3","2","1","N/A"];

  return(
    <>
      <div className="tabs">
        <button className={`tab ${tab==="list"?"active":""}`} onClick={()=>{setTab("list");setEditId(null);setForm(defForm);}}>{E.listTab}</button>
        {isAdmin&&<button className={`tab ${tab==="add"?"active":""}`} onClick={()=>{setTab("add");setEditId(null);setForm(defForm);}}>{E.addTab}</button>}
      </div>
      {tab==="add"&&(
        <div className="card">
          <div className="ct"><Star size={15}/> {editId?E.editTitle:E.addTitle}</div>
          <div className="fg">
            <div className="fl"><label>{E.empSel}</label>
              <select value={form.emp_name} onChange={e=>sf("emp_name",e.target.value)}>
                <option value="">{E.placeholder}</option>
                {employees.filter(e=>e.status!=="Inactive").map(e=>{
                  const days=e.join_date?(new Date()-new Date(e.join_date))/86400000:9999;
                  const isProbation=days<=90;
                  return <option key={e.id} value={e.name}>{e.name}{isProbation?(KO?" 🔶 수습":SP?" 🔶 Período de prueba":" 🔶 Probation"):""}</option>;
                })}
              </select>
            </div>
            <div className="fl"><label>{E.evaluatorLbl}</label><input value={form.evaluator} onChange={e=>sf("evaluator",e.target.value)}/></div>
            <div className="fl"><label>{E.periodLbl}</label>
              <div style={{display:"flex",gap:6}}>
                <select value={(form.period||fmtDate(new Date()).slice(0,7)).split("-")[0]} onChange={e=>{const mm=(form.period||"").split("-")[1]||"01";sf("period",`${e.target.value}-${mm}`);}}>
                  {Array.from({length:6},(_,i)=>new Date().getFullYear()-3+i).map(y=><option key={y} value={y}>{y}</option>)}
                </select>
                <select value={(form.period||fmtDate(new Date()).slice(0,7)).split("-")[1]} onChange={e=>{const yy=(form.period||"").split("-")[0]||String(new Date().getFullYear());sf("period",`${yy}-${e.target.value}`);}}>
                  {t.months.map((m,i)=><option key={i} value={String(i+1).padStart(2,"0")}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="fl"><label>{E.evalDateLbl}</label><input type="date" value={form.eval_date} onChange={e=>sf("eval_date",e.target.value)}/></div>
            <div className="fl ff"><label>{E.goalsLbl}</label><textarea rows={2} value={form.goals} onChange={e=>sf("goals",e.target.value)}/></div>
            <div className="fl ff"><label>{E.outstandingLbl}</label><textarea rows={2} value={form.outstanding} onChange={e=>sf("outstanding",e.target.value)}/></div>
            <div className="fl ff"><label>{E.improveLbl}</label><textarea rows={2} value={form.improve} onChange={e=>sf("improve",e.target.value)}/></div>
            <div className="fl ff"><label>{E.readinessLbl}</label><textarea rows={2} value={form.readiness} onChange={e=>sf("readiness",e.target.value)}/></div>
          </div>
          <div style={{marginTop:16,marginBottom:8}}>
            <div className="ct"> {E.scoreTitle}</div>
            <div style={{overflowX:"auto"}}>
              <table style={{fontSize:13}}>
                <thead><tr>
                  <th style={{minWidth:220,background:"var(--sf2)",color:"var(--tx)",fontWeight:700}}>{lang==="kr"?"역량":lang==="es"?"Competencia":"Competency"}</th>
                  {rOpts.map(r=><th key={r} style={{background:"var(--sf2)",color:"var(--tx)",fontWeight:700,textAlign:"center",width:50}}>{r}</th>)}
                  <th style={{background:"var(--sf2)",color:"var(--tx)",fontWeight:700,minWidth:140}}>{lang==="kr"?"설명":lang==="es"?"Descripción":"Description"}</th>
                </tr></thead>
                <tbody>{E.items.map(item=>(
                  <tr key={item.key}>
                    <td style={{fontSize:12}}>{item.label}</td>
                    {rOpts.map(r=>(
                      <td key={r} style={{textAlign:"center",cursor:"pointer",background:(form.ratings[item.key]||"3")===r?"rgba(74,122,181,.15)":"",transition:"background .1s"}}
                        onClick={()=>sr(item.key,r)}>
                        {(form.ratings[item.key]||"3")===r?<CheckSquare size={14} color="var(--ac)"/>:<Square size={14} color="var(--tx3)"/>}
                      </td>
                    ))}
                    <td><input style={{width:"100%",background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:6,padding:"4px 8px",color:"var(--tx)",fontSize:12,fontFamily:"inherit"}}
                      value={form.descriptions[item.key]||""} onChange={e=>sd(item.key,e.target.value)}/></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
          <div className="fg" style={{marginTop:12}}>
            <div className="fl ff"><label>{E.nextGoalsLbl}</label><textarea rows={2} value={form.next_goals} onChange={e=>sf("next_goals",e.target.value)}/></div>
            <div className="fl ff"><label>{E.nextDevLbl}</label><textarea rows={2} value={form.next_dev} onChange={e=>sf("next_dev",e.target.value)}/></div>
            <div className="fl ff"><label>{E.empCommentLbl}</label><textarea rows={2} value={form.emp_comment} onChange={e=>sf("emp_comment",e.target.value)}/></div>
          </div>
          <div style={{marginTop:14,display:"flex",gap:8}}>
            <button className="btn bp" onClick={save}>{E.save}</button>
            <button className="btn bsec" onClick={()=>{setTab("list");setEditId(null);setForm(defForm);}}>{E.cancel}</button>
          </div>
        </div>
      )}
      {tab==="list"&&(
        <>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
            <button onClick={()=>{setShowSearchPerf(s=>!s);if(showSearchPerf)setSearchPerf("");}} style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",cursor:"pointer",fontSize:16,color:"var(--tx2)",lineHeight:1}}><Search size={15}/></button>
            {showSearchPerf&&<input autoFocus value={searchPerf} onChange={e=>setSearchPerf(e.target.value)}
              placeholder={KO?"이름, 평가자, 기간 검색...":SP?"Buscar nombre, evaluador, período...":"Search name, evaluator, period..."}
              style={{flex:1,padding:"6px 14px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13}}
            />}
          </div>
          {filtered.length===0?<div className="card"><div className="empty">{E.noEval}</div></div>:filtered.map(ev=>(
            <div className="card" key={ev.id}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                    <span className="etag"><span className="edot"/>{ev.emp_name}</span>
                    <span style={{fontFamily:"'DM Mono',monospace",fontSize:13,color:"var(--tx2)"}}>{ev.eval_date}</span>
                    {ev.evaluator&&<span style={{fontSize:12,color:"var(--tx3)"}}>by {ev.evaluator}</span>}
                  </div>
                  <span style={{padding:"3px 8px",borderRadius:20,fontSize:11,fontWeight:700,marginRight:4,
                    background:ev.evaluator_signed_url?"rgba(5,150,105,.12)":"rgba(217,119,6,.12)",
                    color:ev.evaluator_signed_url?"var(--gr)":"var(--ye)"}}>
                    {KO?"평가자: ":SP?"Evaluador: ":"Eval: "}{ev.evaluator_signed_url?(KO?"서명완료":SP?"Firmado":"Signed"):(KO?"서명대기":SP?"Pendiente":"Pending")}
                  </span>
                  <span style={{padding:"3px 8px",borderRadius:20,fontSize:11,fontWeight:700,
                    background:ev.signed_url?"rgba(5,150,105,.12)":"rgba(217,119,6,.12)",
                    color:ev.signed_url?"var(--gr)":"var(--ye)"}}>
                    {KO?"직원: ":SP?"Empleado: ":"Emp: "}{ev.signed_url?(KO?"서명완료":SP?"Firmado":"Signed"):(KO?"서명대기":SP?"Pendiente":"Pending")}
                  </span>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                   {(ev.signed_url&&ev.evaluator_signed_url)&&<button className="btn bs" style={{background:"var(--ac)",color:"#fff"}} onClick={()=>downloadPdf(ev)}>{E.downloadPdf}</button>}
                   {!isAdmin&&!ev.signed_url&&<button className="btn bs" style={{background:"#1a1a1a",color:"#fff"}} onClick={()=>setSignModal(ev.id)}><PenLine size={14} style={{marginRight:4}}/>{KO?"서명하기":SP?"Firmar":"Sign"}</button>}
 
                   {isAdmin&&!ev.evaluator_signed_url&&<button className="btn bs" style={{background:"#1a1a1a",color:"#fff"}} onClick={()=>setSignModal(ev.id)}><PenLine size={14} style={{marginRight:4}}/>{KO?"서명하기":SP?"Firmar":"Sign"}</button>}
 

                   {isAdmin&&<button className="btn bs" style={{background:"var(--sf2)",color:"var(--tx2)",border:"1px solid var(--bd)"}} onClick={()=>startEdit(ev)}>{E.edit}</button>}
                   {isAdmin&&<button className="btn bs bd2" onClick={()=>del(ev.id)}>{E.del}</button>}
                </div>
              </div>
              {(ev.outstanding||ev.improve)&&(
                <div style={{marginTop:12,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  {ev.outstanding&&<div style={{background:"rgba(5,150,105,.06)",borderRadius:8,padding:"8px 12px",fontSize:12}}><b style={{color:"var(--gr)"}}><CheckCircle size={13} style={{marginRight:4}}/>{E.outstandingLbl}:</b><br/>{ev.outstanding}</div>}
                  {ev.improve&&<div style={{background:"rgba(220,38,38,.06)",borderRadius:8,padding:"8px 12px",fontSize:12}}><b style={{color:"var(--re)"}}><AlertTriangle size={13} style={{marginRight:4}}/>{E.improveLbl}:</b><br/>{ev.improve}</div>}
                </div>
              )}
            </div>
          ))}
        </>
      )}
      {signModal&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#fff",borderRadius:16,padding:28,width:"100%",maxWidth:480,boxShadow:"0 8px 40px rgba(0,0,0,.2)"}}>
            <div style={{fontWeight:800,fontSize:17,marginBottom:6,color:"#1a1a1a"}}>{KO?"전자 서명":SP?"Firma electrónica":"Electronic Signature"}</div>
            <div style={{fontSize:12,color:"#888",marginBottom:16}}>{KO?"아래 영역에 서명해 주세요":SP?"Firme en el área de abajo":"Please sign in the area below"}</div>
            <canvas ref={canvasRef} width={420} height={160}
              style={{border:"2px solid #e0e0e0",borderRadius:8,cursor:"crosshair",touchAction:"none",width:"100%",background:"#fafafa"}}
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
            />
            <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
              <button onClick={clearCanvas} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #e0e0e0",background:"#f5f5f5",cursor:"pointer",fontWeight:600,fontSize:13}}>
                {KO?"지우기":SP?"Borrar":"Clear"}
              </button>
              <button onClick={()=>setSignModal(null)} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #e0e0e0",background:"#f5f5f5",cursor:"pointer",fontWeight:600,fontSize:13}}>
                {KO?"취소":SP?"Cancelar":"Cancel"}
              </button>
              <button onClick={submitSignature} disabled={signing} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"#1a1a1a",color:"#fff",cursor:signing?"not-allowed":"pointer",fontWeight:700,fontSize:13,opacity:signing?.7:1}}>
                {signing?"...":(KO?"서명 완료":SP?"Enviar":"Submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NewEmpEval({t,lang,isAdmin,empName,refreshKey}){
  const E=t.eval;
  const KO=lang==="kr"; const SP=lang==="es";
  const [evals,setEvals]=useState([]);
  const [signModal2,setSignModal2]=useState(null); // {id, role: "eval1"|"eval2"}
  const [signing2,setSigning2]=useState(false);
  const canvasRef2=useRef(null);
  const [isDrawing2,setIsDrawing2]=useState(false);
  const [tab,setTab]=useState("list");
  const [editId,setEditId]=useState(null);
  const [searchNew,setSearchNew]=useState("");
  const [showSearchNew,setShowSearchNew]=useState(false);
  const defRatings=E.newCriteria.reduce((a,_,i)=>({...a,[i]:""}),{});
  const defForm={emp_name:"",emp_num:"",dept:"",hire_date:"",eval1:"",eval2:"",eval_date:"",
    ratings:{...defRatings},strength:"",improve:"",prev_goal:"",next_goal:"",
    emp_comment:"",eval1_comment:"",eval2_comment:""};
  const [form,setForm]=useState(defForm);

  useEffect(()=>{ fetchEvals(); },[refreshKey]);
  async function fetchEvals(){let q=supabase.from("new_emp_evals").select("*");if(!isAdmin&&empName)q=q.eq("emp_name",empName);const{data}=await q.order("eval_date",{ascending:false});if(data)setEvals(data);}
  
  function sr(i,v){setForm(p=>({...p,ratings:{...p.ratings,[i]:v}}))}

  function startEdit(ev){
    setForm({emp_name:ev.emp_name||"",emp_num:ev.emp_num||"",dept:ev.dept||"",hire_date:ev.hire_date||"",
      eval1:ev.eval1||"",eval2:ev.eval2||"",eval_date:ev.eval_date||"",
      ratings:ev.ratings||{...defRatings},strength:ev.strength||"",improve:ev.improve||"",
      prev_goal:ev.prev_goal||"",next_goal:ev.next_goal||"",
      emp_comment:ev.emp_comment||"",eval1_comment:ev.eval1_comment||"",eval2_comment:ev.eval2_comment||""});
    setEditId(ev.id); setTab("add");
  }
  async function save(){
    if(!form.emp_name?.trim()) return alert(lang==="kr"?"신입 직원 이름을 입력해주세요.":lang==="es"?"Ingrese el nombre del nuevo empleado.":"Please enter new employee name.");
    if(!form.eval1?.trim()) return alert(lang==="kr"?"평가자 1을 입력해주세요.":lang==="es"?"Ingrese al Evaluador 1.":"Please enter Evaluator 1.");
    if(editId) await supabase.from("new_emp_evals").update(form).eq("id",editId);
    else await supabase.from("new_emp_evals").insert([form]);
    setForm(defForm); setEditId(null); fetchEvals(); setTab("list");
  }
  async function del(id){if(window.confirm(E.confirmDel)){await supabase.from("new_emp_evals").delete().eq("id",id);fetchEvals();}}

    function startDraw2(e){
    setIsDrawing2(true);
    const canvas=canvasRef2.current;
    if(!canvas) return;
    const ctx=canvas.getContext("2d");
    const r=canvas.getBoundingClientRect();
    const scaleX=canvas.width/r.width, scaleY=canvas.height/r.height;
    const cx=e.touches?e.touches[0].clientX:e.clientX;
    const cy=e.touches?e.touches[0].clientY:e.clientY;
    ctx.beginPath(); ctx.moveTo((cx-r.left)*scaleX,(cy-r.top)*scaleY);
  }
  function draw2(e){
    if(!isDrawing2) return;
    e.preventDefault();
    const canvas=canvasRef2.current;
    if(!canvas) return;
    const ctx=canvas.getContext("2d");
    const r=canvas.getBoundingClientRect();
    const scaleX=canvas.width/r.width, scaleY=canvas.height/r.height;
    const cx=e.touches?e.touches[0].clientX:e.clientX;
    const cy=e.touches?e.touches[0].clientY:e.clientY;
    ctx.lineWidth=2.5; ctx.lineCap="round"; ctx.lineJoin="round"; ctx.strokeStyle="#1a1a1a";
    ctx.lineTo((cx-r.left)*scaleX,(cy-r.top)*scaleY); ctx.stroke();
  }
  function endDraw2(){ setIsDrawing2(false); }
  function clearCanvas2(){
    const canvas=canvasRef2.current;
    if(!canvas) return;
    canvas.getContext("2d").clearRect(0,0,canvas.width,canvas.height);
  }
  async function submitSignature2(){
    const canvas=canvasRef2.current;
    if(!canvas) return;
    const imgData=canvas.getContext("2d").getImageData(0,0,canvas.width,canvas.height).data;
    if([...imgData].every(v=>v===0)){ alert(KO?"서명을 그려주세요.":SP?"Por favor dibuje su firma.":"Please draw your signature."); return; }
    setSigning2(true);
    try{
      const blob=await new Promise(res=>canvas.toBlob(res,"image/png"));
      const{id,role}=signModal2;
      const path=`new_emp_signed/${id}_${role}_${Date.now()}.png`;
      const{error}=await supabase.storage.from("hr-files").upload(path,blob,{upsert:true,contentType:"image/png"});
      if(error) throw error;
      const{data:u}=supabase.storage.from("hr-files").getPublicUrl(path);
      const signDate=new Date().toLocaleDateString("en-US");
      const update=role==="eval1"?{eval1_signed_url:u.publicUrl,eval1_signed_at:signDate}:{eval2_signed_url:u.publicUrl,eval2_signed_at:signDate};
      await supabase.from("new_emp_evals").update(update).eq("id",id);
      setSignModal2(null); setSigning2(false);
      fetchEvals();
    }catch(err){ setSigning2(false); alert("Error: "+err.message); }
  }
  function downloadPdf(rec){
    const ratingCols=EN.eval.newRatings;
    const rows=EN.eval.newCriteria.map((crit,i)=>`
      <tr><td>${crit}</td>${ratingCols.map((_,j)=>`<td style="text-align:center">${rec.ratings?.[i]===String(j)?<CheckSquare size={14} color="var(--ac)"/>:<Square size={14} color="var(--tx3)"/>}</td>`).join("")}</tr>`).join("");
    const footer="This executed document serves as official legal proof of performance evaluation completion and may be used for employment records, personnel decisions, legal proceedings, and HR documentation purposes.";
    const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>New Employee Eval - ${rec.emp_name}</title>
    <style>
      @page{margin:0.85in 0.75in 0.75in; @bottom-center{content:"This executed document serves as official legal proof of performance evaluation completion and may be used for employment records, personnel decisions, legal proceedings, and HR documentation purposes.";font-size:6.5pt;color:#555;font-family:Arial,sans-serif;text-align:center;}}
      body{font-family:Arial,sans-serif;font-size:10pt;color:#222;}
      .content{flex:1;}
      .ph{display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #aaa;padding-bottom:6px;margin-bottom:10px;}
      .logo{font-weight:900;font-size:13pt;letter-spacing:1px;}
      .docno{font-size:8pt;color:#888;}
      h1{text-align:center;font-size:15pt;margin:8px 0 14px;}
      .section{font-weight:700;margin:12px 0 5px;}
      table{width:100%;border-collapse:collapse;font-size:9pt;margin-bottom:8px;}
      th{background:#1e3a5f;color:white;padding:5px 8px;text-align:center;}
      td{border:1px solid #ccc;padding:5px 8px;}
      .info td:first-child{font-weight:700;background:#f0f4f9;width:140px;}
      .line{display:block;border-bottom:1px solid #999;width:100%;height:14px;margin:8px 0 4px;}
      .sig-box{border:1px solid #ccc;padding:0;margin-bottom:10px;overflow:hidden;}
      .sig-box h3{background:#1e3a5f;color:white;margin:0 0 10px;padding:5px 10px;font-size:9pt;}
      .sig-box-body{padding:0 10px 10px;}
    </style></head><body>
    <div class="ph"><div class="logo">SPRINGBLINDS</div><div class="docno">Document No.: SB-NEEF-001</div></div>
    <h1>New Employee Evaluation Form</h1>
    <div class="section">1. Employee Information</div>
    <table class="info"><tr><td>New Employee Name</td><td>${rec.emp_name}</td><td>Department</td><td>${rec.dept||""}</td></tr>
    <tr><td>New Employee Number</td><td>${rec.emp_num||""}</td><td>Hire Date</td><td>${rec.hire_date||""}</td></tr></table>
    <div class="section">2. Evaluator</div>
    <table class="info"><tr><td>Evaluator Name 1</td><td>${rec.eval1}</td><td>Evaluator Name 2</td><td>${rec.eval2||""}</td></tr>
    <tr><td>Evaluation Date</td><td>${rec.eval_date||""}</td><td></td><td></td></tr></table>
    <div class="section">3. Work Behavior Assessment</div>
    <p style="font-size:8pt;color:#555;margin:2px 0 6px;">Rating Scale: 5=Exceptional | 4=Highly Effective | 3=Proficient | 2=Inconsistent | 1=Unsatisfactory | N/A=Not Applicable</p>
    <table><thead><tr><th style="text-align:left;width:200px">Evaluation Criteria</th>${ratingCols.map(r=>`<th>${r}</th>`).join("")}</tr></thead>
    <tbody>${rows}</tbody></table>
    <div class="section">4. Strengths / Training Needs</div>
    <table class="info"><tr><td>Employee's Key Strengths</td><td>${rec.strength||""}</td></tr>
    <tr><td>Areas Requiring Improvement</td><td>${rec.improve||""}</td></tr></table>
    <div style="page-break-before:always"></div>
    <div class="section">5. Goals</div>
    <table class="info"><tr><td>Achievement of Goals Set in Previous Evaluation</td><td>${rec.prev_goal||""}</td></tr>
    <tr><td>Goals for Next Evaluation Period</td><td>${rec.next_goal||""}</td></tr></table>
    <div class="section">6. Comments and Approval</div>

    <div class="sig-box"><h3>Evaluator 1</h3><div class="sig-box-body"><p>Comments: ${rec.eval1_comment||""}</p><p>Signature:</p>${rec.eval1_signed_url?`<img src="${rec.eval1_signed_url}" style="height:55px;max-width:100%;object-fit:contain;display:block;margin-bottom:4px;" crossorigin="anonymous"/>`:`<span class="line"/>`}<p>Print Name: <b>${rec.eval1||"___________________"}</b> &nbsp;&nbsp; Date: <b>${rec.eval1_signed_at||"________"}</b></p></div></div>
    ${(rec.eval2||rec.eval2_signed_url)?`<div class="sig-box"><h3>Evaluator 2</h3><div class="sig-box-body"><p>Comments: ${rec.eval2_comment||""}</p><p>Signature:</p>${rec.eval2_signed_url?`<img src="${rec.eval2_signed_url}" style="height:55px;max-width:100%;object-fit:contain;display:block;margin-bottom:4px;" crossorigin="anonymous"/>`:`<span class="line"/>`}<p>Print Name: <b>${rec.eval2||"___________________"}</b> &nbsp;&nbsp; Date: <b>${rec.eval2_signed_at||"________"}</b></p></div></div>`:""}
    </div>
    <script>window.onload=()=>{window.print();}</script>
    </body></html>`;
    const w=window.open("","_blank"); w.document.write(html); w.document.close();
  }

  return(
    <>
      <div className="tabs">
        <button className={`tab ${tab==="list"?"active":""}`} onClick={()=>{setTab("list");setEditId(null);setForm(defForm);}}>{E.newListTab}</button>
        {isAdmin&&<button className={`tab ${tab==="add"?"active":""}`} onClick={()=>{setTab("add");setEditId(null);setForm(defForm);}}>{E.newAddTab}</button>}
      </div>
      {tab==="add"&&(
        <div className="card">
          <div className="ct"><Star size={15}/> {editId?E.editTitle:E.newEmpTitle}</div>
          <div className="fg">
            <div className="fl"><label>{E.newEmpNameLbl}</label><input value={form.emp_name} onChange={e=>sf("emp_name",e.target.value)} onBlur={e=>sf("emp_name",e.target.value)}/></div>
            <div className="fl"><label>{E.newEmpNumLbl}</label><input value={form.emp_num} onChange={e=>sf("emp_num",e.target.value)}/></div>
            <div className="fl"><label>{E.newDeptLbl}</label><input value={form.dept} onChange={e=>sf("dept",e.target.value)}/></div>
            <div className="fl"><label>{E.newHireLbl}</label><input type="date" value={form.hire_date} onChange={e=>sf("hire_date",e.target.value)}/></div>
            <div className="fl"><label>{E.newEval1Lbl}</label><input value={form.eval1} onChange={e=>sf("eval1",e.target.value)}/></div>
            <div className="fl"><label>{E.newEval2Lbl}</label><input value={form.eval2} onChange={e=>sf("eval2",e.target.value)}/></div>
            <div className="fl"><label>{E.newEvalDateLbl}</label><input type="date" value={form.eval_date} onChange={e=>sf("eval_date",e.target.value)}/></div>
          </div>
          <div style={{marginTop:12}}>
            <div className="ct"> {lang==="kr"?"업무 행동 평가":lang==="es"?"Evaluación de conducta laboral":"Work Behavior Assessment"}</div>
            <div style={{overflowX:"auto"}}>
              <table style={{fontSize:12}}>
                <thead><tr>
                  <th style={{background:"var(--sf2)",color:"var(--tx)",textAlign:"left",minWidth:160}}>{lang==="kr"?"평가 항목":lang==="es"?"Criterios de evaluación":"Evaluation Criteria"}</th>
                  {E.newRatings.map(r=><th key={r} style={{background:"var(--sf2)",color:"var(--tx)",width:80}}>{r}</th>)}
                </tr></thead>
                <tbody>{E.newCriteria.map((crit,i)=>(
                  <tr key={i}>
                    <td style={{fontSize:12}}>{crit}</td>
                    {E.newRatings.map((_,j)=>(
                      <td key={j} style={{textAlign:"center",cursor:"pointer",background:form.ratings[i]===String(j)?"rgba(74,122,181,.15)":""}}
                        onClick={()=>sr(i,String(j))}>
                        {form.ratings[i]===String(j)?<CheckSquare size={14} color="var(--ac)"/>:<Square size={14} color="var(--tx3)"/>}
                      </td>
                    ))}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
          <div className="fg" style={{marginTop:12}}>
            <div className="fl"><label>{E.newStrengthLbl}</label><textarea rows={2} value={form.strength} onChange={e=>sf("strength",e.target.value)}/></div>
            <div className="fl"><label>{E.newImproveLbl}</label><textarea rows={2} value={form.improve} onChange={e=>sf("improve",e.target.value)}/></div>
            <div className="fl"><label>{E.newPrevGoalLbl}</label><textarea rows={2} value={form.prev_goal} onChange={e=>sf("prev_goal",e.target.value)}/></div>
            <div className="fl"><label>{E.newNextGoalLbl}</label><textarea rows={2} value={form.next_goal} onChange={e=>sf("next_goal",e.target.value)}/></div>
            <div className="fl ff"><label>{E.newEmpCommentLbl}</label><textarea rows={2} value={form.emp_comment} onChange={e=>sf("emp_comment",e.target.value)}/></div>
            <div className="fl"><label>{E.newEval1CommentLbl}</label><textarea rows={2} value={form.eval1_comment} onChange={e=>sf("eval1_comment",e.target.value)}/></div>
            <div className="fl"><label>{E.newEval2CommentLbl}</label><textarea rows={2} value={form.eval2_comment} onChange={e=>sf("eval2_comment",e.target.value)}/></div>
          </div>
          <div style={{marginTop:14,display:"flex",gap:8}}>
            <button className="btn bp" onClick={save}>{E.save}</button>
            <button className="btn bsec" onClick={()=>{setTab("list");setEditId(null);setForm(defForm);}}>{E.cancel}</button>
          </div>
        </div>
      )}
      {tab==="list"&&(
        <>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
          <button onClick={()=>{setShowSearchNew(s=>!s);if(showSearchNew)setSearchNew("");}} style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",cursor:"pointer",fontSize:16,color:"var(--tx2)",lineHeight:1}}><Search size={15}/></button>
          {showSearchNew&&<input autoFocus value={searchNew} onChange={e=>setSearchNew(e.target.value)}
            placeholder={KO?"이름, 부서 검색...":SP?"Buscar nombre, depto...":"Search name, dept..."}
            style={{flex:1,padding:"6px 14px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13}}
          />}
        </div>
        {evals.filter(ev=>{if(!searchNew)return true;const q=searchNew.toLowerCase();return ev.emp_name?.toLowerCase().includes(q)||ev.dept?.toLowerCase().includes(q)||ev.eval1?.toLowerCase().includes(q);}).length===0
          ?<div className="card"><div className="empty">{E.noNewEval}</div></div>
          :evals.filter(ev=>{if(!searchNew)return true;const q=searchNew.toLowerCase();return ev.emp_name?.toLowerCase().includes(q)||ev.dept?.toLowerCase().includes(q)||ev.eval1?.toLowerCase().includes(q);}).map(ev=>(
          <div className="card" key={ev.id}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                  <span style={{fontWeight:700,fontSize:15}}>{ev.emp_name}</span>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:13,color:"var(--tx2)"}}>{ev.eval_date}</span>
                </div>
                <div style={{fontSize:12,color:"var(--tx2)",marginBottom:6}}>{ev.dept&&<span>{ev.dept}&nbsp;&nbsp;</span>}{ev.eval1&&<span>{ev.eval1}</span>}</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <span style={{padding:"3px 8px",borderRadius:20,fontSize:11,fontWeight:700,background:ev.eval1_signed_url?"rgba(5,150,105,.12)":"rgba(217,119,6,.12)",color:ev.eval1_signed_url?"var(--gr)":"var(--ye)"}}>
                    {KO?"평가자1: ":SP?"Evaluador 1: ":"Eval1: "}{ev.eval1_signed_url?(KO?"서명완료":SP?"Firmado":"Signed"):(KO?"서명대기":SP?"Pendiente":"Pending")}
                  </span>
                  <span style={{padding:"3px 8px",borderRadius:20,fontSize:11,fontWeight:700,background:ev.eval2_signed_url?"rgba(5,150,105,.12)":"rgba(217,119,6,.12)",color:ev.eval2_signed_url?"var(--gr)":"var(--ye)"}}>
                    {KO?"평가자2: ":SP?"Evaluador 2: ":"Eval2: "}{ev.eval2_signed_url?(KO?"서명완료":SP?"Firmado":"Signed"):(KO?"서명대기":SP?"Pendiente":"Pending")}
                  </span>
                </div>
              </div>
              <div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {(ev.eval1_signed_url&&ev.eval2_signed_url)&&<button className="btn bs" style={{background:"var(--ac)",color:"#fff"}} onClick={()=>downloadPdf(ev)}>{E.downloadPdf}</button>}
                  {!ev.eval1_signed_url&&<button className="btn bs" style={{background:"#1a1a1a",color:"#fff"}} onClick={()=>setSignModal2({id:ev.id,role:"eval1"})}><PenLine size={14} style={{marginRight:4}}/>{KO?"평가자1 서명":SP?"Firma Evaluador 1":"Eval1 Sign"}</button>}
                  {ev.eval1_signed_url&&!ev.eval2_signed_url&&<button className="btn bs" style={{background:"#444",color:"#fff"}} onClick={()=>setSignModal2({id:ev.id,role:"eval2"})}><PenLine size={14} style={{marginRight:4}}/>{KO?"평가자2 서명":SP?"Firma Evaluador 2":"Eval2 Sign"}</button>}
                  <button className="btn bs" style={{background:"var(--sf2)",color:"var(--tx2)",border:"1px solid var(--bd)"}} onClick={()=>startEdit(ev)}>{E.edit}</button>
                  <button className="btn bs bd2" onClick={()=>del(ev.id)}>{E.del}</button>
                </div>
              </div>
            </div>
          </div>
        ))
        }</>
      )}
      {signModal2&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#fff",borderRadius:16,padding:28,width:"100%",maxWidth:480,boxShadow:"0 8px 40px rgba(0,0,0,.2)"}}>
            <div style={{fontWeight:800,fontSize:17,marginBottom:6,color:"#1a1a1a"}}>{signModal2?.role==="eval1"?(KO?"평가자1 서명":SP?"Firma del Evaluador 1":"Evaluator 1 Signature"):(KO?"평가자2 서명":SP?"Firma del Evaluador 2":"Evaluator 2 Signature")}</div>
            <div style={{fontSize:12,color:"#888",marginBottom:16}}>{KO?"아래 영역에 서명해 주세요":SP?"Firme en el área de abajo":"Please sign in the area below"}</div>
            <canvas ref={canvasRef2} width={420} height={160}
              style={{border:"2px solid #e0e0e0",borderRadius:8,cursor:"crosshair",touchAction:"none",width:"100%",background:"#fafafa"}}
              onMouseDown={startDraw2} onMouseMove={draw2} onMouseUp={endDraw2} onMouseLeave={endDraw2}
              onTouchStart={startDraw2} onTouchMove={draw2} onTouchEnd={endDraw2}
            />
            <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
              <button onClick={clearCanvas2} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #e0e0e0",background:"#f5f5f5",cursor:"pointer",fontWeight:600,fontSize:13}}>{KO?"지우기":SP?"Borrar":"Clear"}</button>
              <button onClick={()=>setSignModal2(null)} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #e0e0e0",background:"#f5f5f5",cursor:"pointer",fontWeight:600,fontSize:13}}>{KO?"취소":SP?"Cancelar":"Cancel"}</button>
              <button onClick={submitSignature2} disabled={signing2} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"#1a1a1a",color:"#fff",cursor:signing2?"not-allowed":"pointer",fontWeight:700,fontSize:13,opacity:signing2?.7:1}}>
                {signing2?"...":(KO?"서명 완료":SP?"Enviar":"Submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PerfNotes({t,lang,isAdmin,empName,refreshKey}){
  const E=t.eval;
  const KO=lang==="kr"; const SP=lang==="es";
  const [employees,setEmp]=useState([]);
  const [notes,setNotes]=useState([]);
  const [tab,setTab]=useState("list");
  const [fe,setFe]=useState("");
  const [searchNote,setSearchNote]=useState("");
  const [showSearchNote,setShowSearchNote]=useState(false);
  const [editId,setEditId]=useState(null);
  const defForm={emp_name:"",date:fmtDate(new Date()),category:E.noteCats[0],content:""};
  const [form,setForm]=useState(defForm);

  useEffect(()=>{
    supabase.from("employees").select("*").order("join_date",{ascending:true}).then(({data})=>data&&setEmp(data));
    fetchNotes();
  },[refreshKey]);
  async function fetchNotes(){const{data}=await supabase.from("perf_notes").select("*").order("date",{ascending:false});if(data)setNotes(data);}
  function sf(k,v){setForm(p=>({...p,[k]:v}));}

  function startEdit(n){
    setForm({emp_name:n.emp_name,date:n.date,category:n.category,content:n.content});
    setEditId(n.id); setTab("add");
  }
  async function save(){
    if(!form.emp_name||!form.content) return alert(E.noteContentLbl);
    if(editId) await supabase.from("perf_notes").update(form).eq("id",editId);
    else await supabase.from("perf_notes").insert([form]);
    setForm(defForm); setEditId(null); fetchNotes(); setTab("list");
  }
  async function del(id){if(window.confirm(E.confirmDel)){await supabase.from("perf_notes").delete().eq("id",id);fetchNotes();}}

  const catColors=["var(--gr)","var(--re)","var(--ye)","var(--ac)","var(--ac2)"];
  const filtered=notes.filter(n=>{
    if(fe&&n.emp_name!==fe) return false;
    if(searchNote){const q=searchNote.toLowerCase();if(!n.emp_name?.toLowerCase().includes(q)&&!n.content?.toLowerCase().includes(q)&&!n.category?.toLowerCase().includes(q)) return false;}
    return true;
  });

  return(
    <>
      <div className="tabs">
        <button className={`tab ${tab==="list"?"active":""}`} onClick={()=>{setTab("list");setEditId(null);setForm(defForm);}}>{E.noteListTab}</button>
        <button className={`tab ${tab==="add"?"active":""}`} onClick={()=>{setTab("add");setEditId(null);setForm(defForm);}}>{E.noteAddTab}</button>
      </div>
      {tab==="add"&&(
        <div className="card">
          <div className="ct"><Star size={15}/> {editId?E.editTitle:E.noteTitle}</div>
          <div className="fg">
            <div className="fl"><label>{E.noteEmpLbl}</label>
              <select value={form.emp_name} onChange={e=>sf("emp_name",e.target.value)}>
                <option value="">{E.placeholder}</option>
                {employees.filter(e=>e.status!=="Inactive").map(e=><option key={e.id} value={e.name}>{e.name}</option>)}
              </select>
            </div>
            <div className="fl"><label>{E.noteDateLbl}</label><input type="date" value={form.date} onChange={e=>sf("date",e.target.value)}/></div>
            <div className="fl ff"><label>{E.noteCatLbl}</label>
              <select value={form.category} onChange={e=>sf("category",e.target.value)}>
                {E.noteCats.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="fl ff"><label>{E.noteContentLbl}</label><textarea rows={4} value={form.content} onChange={e=>sf("content",e.target.value)} placeholder={E.noteContentPh}/></div>
          </div>
          <div style={{marginTop:14,display:"flex",gap:8}}>
            <button className="btn bp" onClick={save}>{E.save}</button>
            <button className="btn bsec" onClick={()=>{setTab("list");setEditId(null);setForm(defForm);}}>{E.cancel}</button>
          </div>
        </div>
      )}
      {tab==="list"&&(
        <>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
            <button onClick={()=>{setShowSearchNote(s=>!s);if(showSearchNote)setSearchNote("");}} style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",cursor:"pointer",fontSize:16,color:"var(--tx2)",lineHeight:1}}><Search size={15}/></button>
            {showSearchNote&&<input autoFocus value={searchNote} onChange={e=>setSearchNote(e.target.value)}
              placeholder={KO?"이름, 카테고리, 내용 검색...":SP?"Buscar nombre, categoría, contenido...":"Search name, category, content..."}
              style={{flex:1,padding:"6px 14px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13}}
            />}
          </div>
          {filtered.length===0?<div className="card"><div className="empty">{E.noNote}</div></div>:filtered.map(n=>(
            <div className="card" key={n.id} style={{borderLeft:`4px solid ${catColors[E.noteCats.indexOf(n.category)]||"var(--ac)"}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                    <span className="etag"><span className="edot"/>{n.emp_name}</span>
                    <span style={{fontSize:11,fontWeight:700,color:catColors[E.noteCats.indexOf(n.category)]||"var(--ac)",background:"var(--sf2)",padding:"2px 8px",borderRadius:10}}>{n.category}</span>
                    <span style={{fontSize:12,color:"var(--tx3)"}}>{n.date}</span>
                  </div>
                  <div style={{fontSize:13,color:"var(--tx)",lineHeight:1.6}}>{n.content}</div>
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button className="btn bs" style={{background:"var(--sf2)",color:"var(--tx2)",border:"1px solid var(--bd)"}} onClick={()=>startEdit(n)}>{E.edit}</button>
                  <button className="btn bs bd2" onClick={()=>del(n.id)}>{E.del}</button>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
}
